import React, { useEffect, useState, useContext } from "react";
import { useLocalization } from "@fluent/react";
import {
  ContentCard,
  LoadingSkeleton,
  Row,
  Toggle,
  Tooltip,
  Button,
  Chip,
} from "@narmi/design_system";
import ReactMarkdown from "react-markdown";
import Organization from "byzantine/src/Organization";
import OrganizationUser from "byzantine/src/OrganizationUser";
import InstitutionSettingsContext, {
  InstitutionSettingsContextProvider,
} from "../contexts/InstitutionSettingsContext";
import ConfirmModal from "../ConfirmModal";
import styles from "./DualApproval.module.scss";
import ApprovalExpirationDialog from "./ApprovalExpirationDialog";
import DisableAdminExemptionDialog from "./DisableAdminExemptionDialog";

type ApprovalType =
  | "WIRE"
  | "INTERNATIONAL_WIRE"
  | "ACH"
  | "ACH_AND_EXPEDITE";

type ApprovalConfig = {
  key: string;
  label: string;
  testId: string;
  featureKey: string;
};

type ApprovalTypesMap = Partial<Record<ApprovalType, ApprovalConfig>>;

type OrganizationState = Organization & { hasMultipleUsers: boolean };

type DualApprovalCheckboxProps = {
  required?: boolean;
  toggle: () => void;
};

const APPROVAL_TYPES: ApprovalTypesMap = {
  WIRE: {
    key: "dual_approval_required",
    label: "Domestic wires",
    testId: "dual-approval-confirm",
    featureKey: "wires",
  },
  INTERNATIONAL_WIRE: {
    key: "international_wire_dual_approval_required",
    label: "International wires",
    testId: "international-wire-dual-approval-confirm",
    featureKey: "international_wires",
  },
};

const DualApprovalOptIn = () => {
  const { l10n } = useLocalization();
  const [orgUuid, setOrgUuid] = useState<string | undefined>(undefined);
  const [orgRole, setOrgRole] = useState<string | undefined>(undefined);
  const [organization, setOrganization] = useState<
    OrganizationState | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [activeModal, setActiveModal] = useState<ApprovalType | null>(null);
  const [approvalTypes, setApprovalTypes] =
    useState<ApprovalTypesMap>(APPROVAL_TYPES);
  const [showApprovalExpirationModal, setShowApprovalExpirationModal] =
    useState(false);
  const [showExemptionConfirm, setShowExemptionConfirm] = useState(false);
  const {
    short_name: shortName,
    features,
    max_days_approval_grace_period: maxDays,
  } = useContext(InstitutionSettingsContext);

  const { ach_payments: achEnabled, expedited_payments: expeditedEnabled } =
    features || {};

  // load org on mount and store it in the DOM (if not already populated)
  useEffect(() => {
    const bootstrapOrgData = async () => {
      setLoading(true);
      const currentOrgUser = await OrganizationUser.currentOrgUser();

      // check if member of an org
      if (!currentOrgUser) {
        setLoading(false);
        return;
      }

      setOrgUuid(currentOrgUser.organization_uuid);
      setOrgRole(currentOrgUser.role.name);

      // load dualApproval status
      const org = await Organization.getOrganization(
        currentOrgUser.organization_uuid as API.OrganizationId
      );

      // load if org has multiple users
      const { organization_users: orgUsers } =
        await OrganizationUser.forOrganization(
          currentOrgUser.organization_uuid
        );
      const hasMultipleUsers =
        Organization.getOrganizationHasMultipleApprovalUsers(orgUsers);

      const updatedOrg = {
        ...org,
        hasMultipleUsers,
      } as OrganizationState;

      setOrganization(() => updatedOrg);
      setLoading(false);
    };
    bootstrapOrgData();
  }, []);

  useEffect(() => {
    const updatedTypes: ApprovalTypesMap = { ...APPROVAL_TYPES };

    if (achEnabled && !expeditedEnabled) {
      updatedTypes.ACH = {
        key: "ach_dual_approval_required",
        label: "ACH payments",
        testId: "ach-dual-approval-confirm",
        featureKey: "ach_payments",
      };
    } else if (achEnabled && expeditedEnabled) {
      updatedTypes.ACH_AND_EXPEDITE = {
        key: "ach_dual_approval_required",
        label: "ACH and instant payments",
        testId: "ach-dual-approval-confirm",
        featureKey: "expedited_payments",
      };
    }

    setApprovalTypes(updatedTypes);
  }, [achEnabled, expeditedEnabled]);

  const showModal = (type: ApprovalType) => setActiveModal(type);
  const closeModal = () => setActiveModal(null);

  const toggleApproval = async (approvalType: ApprovalType) => {
    const config = approvalTypes[approvalType]!;

    const orgDetails = await Organization.setOrganization(orgUuid! as API.OrganizationId, "PATCH", {
      [config.key]: !(organization as any)[config.key],
    });
    const updatedOrg = {
      ...organization,
      [config.key]: (orgDetails as any)[config.key],
      requires_some_dual_approvals: orgDetails.requires_some_dual_approvals,
      dual_approval_user_control: orgDetails.dual_approval_user_control,
    } as OrganizationState;
    setOrganization(() => updatedOrg);
    return updatedOrg;
  };

  const handleToggleApproval = (approvalType: ApprovalType) => {
    const config = approvalTypes[approvalType]!;

    // can't turn off unless dual_approval_user_control is enabled
    if (
      (organization as any)[config.key] &&
      !organization!.dual_approval_user_control
    ) {
      return false;
    }

    if (!(organization as any)[config.key]) {
      return showModal(approvalType);
    }
    return toggleApproval(approvalType);
  };

  const handleConfirmToggle = async (approvalType: ApprovalType) => {
    await toggleApproval(approvalType);
    closeModal();
  };

  const handleToggleExpirationModal = () => {
    setShowApprovalExpirationModal(!showApprovalExpirationModal);
  };

  const handleSaveExpirationDays = async (days: number) => {
    const org = await Organization.setOrganization(orgUuid! as API.OrganizationId, "PATCH", {
      ach_dual_approval_expiration_grace_period_days: days,
    });
    const updatedOrg = {
      ...organization,
      ach_dual_approval_expiration_grace_period_days:
        org.ach_dual_approval_expiration_grace_period_days,
    } as OrganizationState;

    setOrganization(updatedOrg);
  };

  const handleDisableExemption = async () => {
    const orgDetails = await Organization.setOrganization(orgUuid! as API.OrganizationId, "PATCH", {
      dual_approval_admin_exemption: false,
    });
    setOrganization(
      (prev) =>
        ({
          ...prev!,
          dual_approval_admin_exemption:
            orgDetails.dual_approval_admin_exemption,
        }) as OrganizationState
    );
    setShowExemptionConfirm(false);
  };

  const hasDualApprovals = features && features.dual_approval;

  // if user doesn't have feature flag for any dual approvals, return nothing
  if (
    !(
      orgRole &&
      OrganizationUser.ROLES_WITH_EDIT_PERMISSION.includes(orgRole) &&
      features &&
      hasDualApprovals
    )
  ) {
    return null;
  }

  const DualApprovalCheckbox = ({
    required,
    toggle,
  }: DualApprovalCheckboxProps) => {
    if (required && !organization!.dual_approval_user_control) {
      return (
        <Tooltip text={l10n.getString("approval-disable-support")}>
          <Toggle
            testId="dual-approval-checkbox"
            isActive={required}
            onChange={toggle}
          />
        </Tooltip>
      );
    }

    if (required) {
      return (
        <Toggle
          testId="dual-approval-checkbox"
          isActive={required}
          onChange={toggle}
        />
      );
    }

    if (!organization!.hasMultipleUsers) {
      return (
        <Tooltip text={l10n.getString("additional-user-tooltip")}>
          <Toggle
            testId="dual-approval-checkbox"
            isActive={required}
            onChange={toggle}
            disabled
          />
        </Tooltip>
      );
    }

    return (
      <Toggle
        testId="dual-approval-checkbox"
        isActive={required}
        onChange={toggle}
      />
    );
  };

  const GRACE_PERIOD_DAYS =
    organization?.ach_dual_approval_expiration_grace_period_days ?? maxDays;

  const renderApprovalSection = (approvalType: string) => {
    const config = approvalTypes[approvalType as ApprovalType];
    if (!config) return null;
    const isFeatureEnabled = (features as any)[config.featureKey];

    if (!isFeatureEnabled) return null;

    return (
      <div className="margin--bottom--m" key={approvalType}>
        <Row>
          <Row.Item>
            <span className="fontSize--l fontWeight--bold">{config.label}</span>
          </Row.Item>
          <Row.Item shrink>
            <LoadingSkeleton isLoading={loading} lines={1}>
              <DualApprovalCheckbox
                required={(organization as any)?.[config.key]}
                toggle={() =>
                  handleToggleApproval(approvalType as ApprovalType)
                }
              />
            </LoadingSkeleton>
          </Row.Item>
        </Row>

        {approvalType === "ACH_AND_EXPEDITE" &&
          (organization as any)?.[config.key] && (
            <>
              <hr className="margin--top--l" />
              <div className={styles.approvalExpirationContainer}>
                <Row>
                  <Row.Item>
                    <span className={styles.sectionLabel}>
                      {l10n.getString("approval-expiration-title")}
                    </span>
                    <p className="margin--top--s fontSize--s">
                      {l10n.getString(
                        "approval-expiration-content-start"
                      )}{" "}
                      {GRACE_PERIOD_DAYS != null && <span className="fontWeight--semibold">
                        {l10n.getString("approval-expiration-days", {
                          days: GRACE_PERIOD_DAYS,
                        })}
                      </span>}{" "}
                      {l10n.getString("approval-expiration-content-end")}
                    </p>
                  </Row.Item>
                  <Row.Item shrink>
                    <Button
                      kind="secondary"
                      size="s"
                      label={l10n.getString("button-edit")}
                      onClick={handleToggleExpirationModal}
                    />
                  </Row.Item>
                </Row>
              </div>
            </>
          )}
      </div>
    );
  };

  return (
    <div id="dual-approvals">
      <ContentCard kind="elevated">
        <h3>{l10n.getString("heading-dual-approvals")}</h3>
        <LoadingSkeleton isLoading={loading} content={"paragraph"}>
          <ReactMarkdown className="margin--bottom--l margin--top--s">
            {l10n.getString("dual-approval-opt-in-description")}
          </ReactMarkdown>
        </LoadingSkeleton>

        <div className={styles.sectionLabel}>
          {l10n.getString("dual-approval-require-approval-for")}
        </div>

        {Object.keys(approvalTypes).map(renderApprovalSection)}

        {organization?.requires_some_dual_approvals && (
          <>
            <hr />
            <div className={styles.adminExemptionContainer}>
              <Row alignItems="center">
                <Row.Item>
                  <span className="fontWeight--bold">
                    {l10n.getString("admin-exemption-label")}
                  </span>{" "}
                  <Tooltip
                    text={l10n.getString(
                      organization.dual_approval_admin_exemption
                        ? "admin-exemption-tooltip-active"
                        : "admin-exemption-tooltip"
                    )}
                  >
                    <span className="narmi-icon-info fontSize--s fontColor--secondary" />
                  </Tooltip>
                </Row.Item>
                <Row.Item shrink>
                  <LoadingSkeleton isLoading={loading} lines={1}>
                    {organization.dual_approval_admin_exemption ? (
                      <Row alignItems="center">
                        <Row.Item shrink>
                          <Button
                            kind="negative"
                            size="s"
                            label={l10n.getString(
                              "admin-exemption-button-turn-off"
                            )}
                            onClick={() =>
                              setShowExemptionConfirm(true)
                            }
                          />
                        </Row.Item>
                        <Row.Item shrink>
                          <Chip
                            kind="success"
                            startIcon="check"
                            label={l10n.getString(
                              "admin-exemption-status-active"
                            )}
                          />
                        </Row.Item>
                      </Row>
                    ) : (
                      <Tooltip
                        text={l10n.getString(
                          "admin-exemption-contact-support-to-enable"
                        )}
                      >
                        <Chip
                          kind="info"
                          startIcon="lock"
                          label={l10n.getString(
                            "admin-exemption-status-off"
                          )}
                        />
                      </Tooltip>
                    )}
                  </LoadingSkeleton>
                </Row.Item>
              </Row>
            </div>
            <div className="margin--top--l">
              <Button
                kind="plain"
                onClick={() => window.location.assign("/support")}
                size="xs"
              >
                <span className="narmi-icon-life-buoy margin--right--xs" />
                {shortName && l10n.getString("admin-exemption-contact-support", {
                  shortName,
                })}
              </Button>
            </div>
          </>
        )}
      </ContentCard>

      {activeModal && (
        <ConfirmModal
          dataTestId={approvalTypes[activeModal]!.testId}
          headerText={l10n.getString("heading-dual-approvals")}
          contentText={l10n.getString("confirm-enable-dual-approvals", {
            shortName: shortName || "",
          })}
          cancelLabel={l10n.getString("button-cancel")}
          confirmLabel={l10n.getString("button-confirm-turn-on")}
          isOpen={true}
          onConfirm={() => handleConfirmToggle(activeModal)}
          onCancel={closeModal}
          onDismiss={closeModal}
        />
      )}

      <ApprovalExpirationDialog
        isOpen={showApprovalExpirationModal}
        handleClose={handleToggleExpirationModal}
        handleSave={handleSaveExpirationDays}
        gracePeriodDays={GRACE_PERIOD_DAYS ?? null}
      />

      <DisableAdminExemptionDialog
        isOpen={showExemptionConfirm}
        onConfirm={handleDisableExemption}
        onDismiss={() => setShowExemptionConfirm(false)}
      />
    </div>
  );
};

const DualApprovalOptInContainer = () => (
  <InstitutionSettingsContextProvider>
    <DualApprovalOptIn />
  </InstitutionSettingsContextProvider>
);

export default DualApprovalOptInContainer;
